import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Text,
  Flex,
  Badge,
  Icon,
  IconButton,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
  Portal,
  Collapse,
} from '@chakra-ui/react';
import {
  FiCode,
  FiSearch,
  FiUser,
  FiUsers,
  FiBriefcase,
  FiSend,
  FiChevronDown,
  FiChevronRight,
  FiZap,
  FiLayers,
} from 'react-icons/fi';
import { useSchemaDiscovery } from '../../../../hooks/useSchemaDiscovery';
import { useCampaignContext } from './CampaignContext';
import useCalScheduling from '../../../../hooks/useCalScheduling';
import { useCrmSettings } from '../../../../contexts/CrmSettingsContext';
import { buildDealTeamVariableEntries } from '../../../../utils/templateVariables';

const OUTPUT_SCHEMAS = {
  SEND_EMAIL: [
    { key: 'to', label: 'Recipient Email' },
    { key: 'subject', label: 'Subject Line' },
    { key: 'senderEmail', label: 'Sender Email' },
  ],
  SEND_SMS: [
    { key: 'to', label: 'Recipient Phone' },
    { key: 'messagePreview', label: 'Message Preview' },
  ],
  SEND_POSTCARD: [
    { key: 'orderId', label: 'PCM Order ID' },
    { key: 'designName', label: 'Design Name' },
    { key: 'to', label: 'Mailing Address' },
    { key: 'mailClass', label: 'Mail Class' },
  ],
  WAIT: [
    { key: 'duration', label: 'Duration' },
    { key: 'unit', label: 'Unit' },
    { key: 'resumeAt', label: 'Resume At' },
  ],
  IF_ELSE: [
    { key: 'result', label: 'Condition Result' },
    { key: 'branch', label: 'Branch Taken' },
    { key: 'fieldValue', label: 'Actual Value' },
  ],
  ADD_NOTE: [{ key: 'notePreview', label: 'Note Preview' }],
  UPDATE_STAGE: [
    { key: 'previousStage', label: 'Previous Stage' },
    { key: 'newStage', label: 'New Stage' },
  ],
  CREATE_TASK: [
    { key: 'taskId', label: 'Task ID' },
    { key: 'title', label: 'Task Title' },
  ],
  MANAGE_TAG: [
    { key: 'tagName', label: 'Tag Name' },
    { key: 'action', label: 'Action' },
  ],
  SEND_WEBHOOK: [
    { key: 'statusCode', label: 'Response Status' },
    { key: 'url', label: 'URL' },
  ],
  APPEND_SHEET_ROW: [
    { key: 'updatedRange', label: 'Updated Range' },
    { key: 'sheetName', label: 'Tab Name' },
  ],
  SEND_NOTIFICATION: [{ key: 'messagePreview', label: 'Message' }],
  UPDATE_DEAL_FIELD: [
    { key: 'fieldName', label: 'Field Name' },
    { key: 'fieldValue', label: 'New Value' },
  ],
  SWITCH: [{ key: 'matchedCase', label: 'Matched Case' }],
  AB_SPLIT: [{ key: 'branch', label: 'Branch' }],
  ENROLL_CAMPAIGN: [{ key: 'targetCampaignId', label: 'Target Campaign' }],
  UNENROLL_CAMPAIGN: [{ key: 'cancelledCount', label: 'Items Cancelled' }],
};

const SENDER_VARIABLES = [
  { key: 'sender.name', label: 'Your Name' },
  { key: 'sender.email', label: 'Your Email' },
  { key: 'sender.phone', label: 'Your Phone' },
  { key: 'sender.nmls', label: 'Your NMLS' },
  { key: 'sender.title', label: 'Your Title' },
  // users/{uid}.website — synced from Google Admin (Broker_Information.Website_Link)
  { key: 'sender.websiteLink', label: 'Your Website Link' },
];

// Cal.diy scheduling pilot — only offered when the per-user flag is on
const BOOKING_LINK_VARIABLE = { key: 'sender.bookingLink', label: 'Your Booking Link' };

const GROUP_ICONS = {
  Contact: FiUser,
  Deal: FiBriefcase,
  Sender: FiSend,
  Trigger: FiZap,
  Step: FiLayers,
};

function VariableGroup({ label, icon, variables, search, onInsert, testValue }) {
  const [isOpen, setIsOpen] = useState(false);
  const headerBg = useColorModeValue('gray.50', 'gray.750');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');

  const filtered = search
    ? variables.filter(
        (v) =>
          v.label.toLowerCase().includes(search) ||
          v.key.toLowerCase().includes(search)
      )
    : variables;

  if (filtered.length === 0) return null;

  return (
    <Box mb={1}>
      <Flex
        align="center"
        gap={1.5}
        px={2}
        py={1.5}
        bg={headerBg}
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ opacity: 0.8 }}
        borderRadius="sm"
      >
        <Icon as={isOpen ? FiChevronDown : FiChevronRight} boxSize={3} color="gray.500" />
        {icon && <Icon as={icon} boxSize={3} color="gray.500" />}
        <Text fontSize="xs" fontWeight="bold" color="gray.600" _dark={{ color: 'gray.300' }}>
          {label}
        </Text>
        <Badge fontSize="9px" colorScheme="gray" ml="auto">
          {filtered.length}
        </Badge>
      </Flex>
      <Collapse in={isOpen}>
        {filtered.map((v) => (
          <Flex
            key={v.key}
            align="center"
            px={3}
            py={1}
            cursor="pointer"
            _hover={{ bg: hoverBg }}
            onClick={() => onInsert(v.key)}
            gap={2}
          >
            <Text fontSize="xs" flex={1}>
              {v.label}
            </Text>
            <Text fontSize="10px" color="gray.400" fontFamily="mono">
              {`{{${v.key}}}`}
            </Text>
          </Flex>
        ))}
      </Collapse>
    </Box>
  );
}

/**
 * Dynamic variable picker that loads available variables from:
 * 1. Schema discovery (all contact + deal columns)
 * 2. Sender fields (static, from Firestore)
 * 3. Previous node outputs (from outputSchemas + test results)
 *
 * Inserts {{variable.path}} at the cursor position in a textarea.
 */
export default function DynamicVariablePicker({
  textareaRef,
  fieldName,
  setFieldValue,
  currentValue,
  currentNodeId,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const { getContactVariableGroups, getDealVariableGroups } = useSchemaDiscovery();
  const { nodes, testResults } = useCampaignContext();
  const { dealPartyRoles } = useCrmSettings();
  // Offer {{bookingLink}} only once the user actually has a default booking
  // page (it resolves to an empty string otherwise).
  const { defaultBookingUrl } = useCalScheduling();
  const senderVariables = defaultBookingUrl
    ? [...SENDER_VARIABLES, BOOKING_LINK_VARIABLE]
    : SENDER_VARIABLES;
  const dealTeamVariables = useMemo(
    () => buildDealTeamVariableEntries(dealPartyRoles),
    [dealPartyRoles]
  );

  const bg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useOutsideClick({ ref: containerRef, handler: onClose });

  const insert = useCallback(
    (key) => {
      const tag = `{{${key}}}`;
      const el = textareaRef?.current;
      if (el) {
        const start = el.selectionStart || 0;
        const end = el.selectionEnd || 0;
        const before = (currentValue || '').slice(0, start);
        const after = (currentValue || '').slice(end);
        setFieldValue(fieldName, before + tag + after);
        setTimeout(() => {
          el.focus();
          const pos = start + tag.length;
          el.setSelectionRange(pos, pos);
        }, 0);
      } else {
        setFieldValue(fieldName, (currentValue || '') + tag);
      }
      onClose();
    },
    [textareaRef, fieldName, setFieldValue, currentValue, onClose]
  );

  const contactGroups = useMemo(() => getContactVariableGroups(), [getContactVariableGroups]);
  const dealGroups = useMemo(() => getDealVariableGroups(), [getDealVariableGroups]);

  const nodeOutputGroups = useMemo(() => {
    if (!currentNodeId) return [];
    const groups = [];
    const currentIdx = nodes.findIndex((n) => n.id === currentNodeId);

    for (let i = 0; i < currentIdx; i++) {
      const prevNode = nodes[i];
      const schema = OUTPUT_SCHEMAS[prevNode.subType] || [];
      if (schema.length === 0) continue;

      const testResult = testResults?.[prevNode.id];
      const vars = schema.map((s) => ({
        key: `nodes.${prevNode.id}.output.${s.key}`,
        label: s.label,
        testValue: testResult?.output?.details?.[s.key],
      }));

      const label = `Step ${i + 1}: ${prevNode.subType.replace(/_/g, ' ')}`;
      groups.push({ label, variables: vars, icon: FiLayers });
    }

    return groups;
  }, [currentNodeId, nodes, testResults]);

  const searchLower = search.toLowerCase();

  return (
    <Box position="relative" ref={containerRef} display="inline-block">
      <Tooltip label="Insert Variable" placement="top" hasArrow openDelay={400}>
        <IconButton
          icon={<FiCode />}
          size="xs"
          variant="ghost"
          aria-label="Insert variable"
          onClick={() => { isOpen ? onClose() : onOpen(); }}
        />
      </Tooltip>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          right={0}
          mt={1}
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          shadow="xl"
          zIndex={1500}
          w="320px"
          maxH="400px"
          overflowY="auto"
        >
          <Box p={2} position="sticky" top={0} bg={bg} zIndex={1} borderBottomWidth="1px" borderColor={borderColor}>
            <InputGroup size="xs">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder="Search variables..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </InputGroup>
          </Box>

          {/* Contact columns (from schema) */}
          {contactGroups.map((g) => (
            <VariableGroup
              key={`c-${g.label}`}
              label={g.label}
              icon={FiUser}
              variables={g.variables}
              search={searchLower}
              onInsert={insert}
            />
          ))}

          {/* Deal columns (from schema) */}
          {dealGroups.map((g) => (
            <VariableGroup
              key={`d-${g.label}`}
              label={g.label}
              icon={FiBriefcase}
              variables={g.variables}
              search={searchLower}
              onInsert={insert}
            />
          ))}

          {/* Deal team roles ({{owner.*}}, {{csc.*}}, {{titleCompany.*}}, ...) */}
          <VariableGroup
            label="Deal Team"
            icon={FiUsers}
            variables={dealTeamVariables}
            search={searchLower}
            onInsert={insert}
          />

          {/* Sender */}
          <VariableGroup
            label="Sender (You)"
            icon={FiSend}
            variables={senderVariables}
            search={searchLower}
            onInsert={insert}
          />

          {/* Previous node outputs */}
          {nodeOutputGroups.map((g) => (
            <VariableGroup
              key={g.label}
              label={g.label}
              icon={g.icon}
              variables={g.variables}
              search={searchLower}
              onInsert={insert}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
